import {
	Content as RadixTooltipContent,
	Arrow as TooltipArrow,
	Portal as TooltipPortal,
	// biome-ignore lint/style/noRestrictedImports: component usage
} from "@radix-ui/react-tooltip";
import { type VariantProp, type VariantProps, cv } from "@suse-o11y/class-variance";
import { SlotHost } from "@suse-o11y/slot";
import React from "react";

import { useIsTooltipHidden } from "./_context";
import { useIsEnabled } from "./_enabler";
import styles from "./_tooltip.module.css";

const variants = cv(styles.content, {
	variants: {
		width: {
			auto: null,
			"trigger-max": styles.widthMax,
			"trigger-min": styles.widthMin,
			"trigger-same": styles.widthSame,
		},
	},

	defaultVariants: {
		width: "auto",
	},
});

/**
 * The width of the tooltip content.
 * - `auto` (default): The width is determined by the content.
 * - `trigger-max`: The width is at most the width of the trigger element.
 * - `trigger-min`: The width is at least the width of the trigger element.
 * - `trigger-same`: The width matches the width of the trigger element.
 */
export type TooltipContentWidth = VariantProp<typeof variants, "width">;

export interface TooltipContentProps
	extends VariantProps<typeof variants>,
		Omit<React.ComponentProps<typeof RadixTooltipContent>, "asChild"> {}

export const TooltipContent = React.forwardRef<React.ComponentRef<typeof RadixTooltipContent>, TooltipContentProps>(
	({ className, width, children, ...props }, ref) => {
		const hidden = useIsTooltipHidden();
		const isEnabled = useIsEnabled();

		if (hidden || !isEnabled || !children) {
			return null;
		}

		return (
			<TooltipPortal>
				<RadixTooltipContent
					arrowPadding={4}
					className={variants({ width, className })}
					collisionPadding={8} // avoid sticking to the edges of the boundaries
					ref={ref} // avoid rendering the arrow on the rounded corners
					sideOffset={4} // distance from the trigger
					{...props}
				>
					<SlotHost>{children}</SlotHost>
					<TooltipArrow className={styles.arrow} height={6} width={12} />
				</RadixTooltipContent>
			</TooltipPortal>
		);
	},
);

if (import.meta.env.DEV) {
	TooltipContent.displayName = "TooltipContent";
}
